import { Component, Input, Output, EventEmitter, ViewChild, signal, OnInit, OnDestroy, AfterContentInit, TemplateRef, ContentChildren, QueryList, DestroyRef, inject, ViewEncapsulation, Injector, afterNextRender } from '@angular/core';
import { ColumnComponent } from '@progress/kendo-angular-grid';
import { FormGroup, FormControl, ReactiveFormsModule } from '@angular/forms';
import { GridComponent, DataStateChangeEvent, GridModule, SelectionEvent } from '@progress/kendo-angular-grid';
import { WindowService, WindowRef, WindowSettings } from '@progress/kendo-angular-dialog';
import { CommonModule } from '@angular/common';
import { SearchField } from './ticomix-search.models';
import { IMvcDataSource } from '../kendo-data-source-mvc';
import { KendoDataSourceMvcDirective } from '../kendo-data-source-mvc';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { ButtonModule } from '@progress/kendo-angular-buttons';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * A configurable grid window component that provides search and selection functionality.
 * 
 * This component combines a Kendo UI Window with a Grid to provide a reusable search/select dialog.
 * It supports custom columns, search fields, and data sources.
 * 
 * @example
 * ```html
 * <!-- Basic usage with minimal configuration -->
 * <ticomix-grid-window
 *   [title]="'Select Customer'"
 *   [width]="900"
 *   [height]="600"
 *   [searchFields]="searchFields"
 *   [dataSource]="customersDataSource"
 *   (rowSelect)="onCustomerSelected($event)">
 *   
 *   <!-- Custom columns -->
 *   <kendo-grid-column field="id" title="ID" width="80"></kendo-grid-column>
 *   <kendo-grid-column field="name" title="Name" width="200"></kendo-grid-column>
 *   <kendo-grid-column field="email" title="Email" width="200"></kendo-grid-column>
 * </ticomix-grid-window>
 * 
 * <!-- Trigger the window -->
 * <button (click)="customerGridWindow.open()">Select Customer</button>
 * ```
 */
@Component({
  selector: 'ticomix-grid-window',
  templateUrl: './ticomix-grid-window.component.html',
  styleUrls: ['./ticomix-grid-window.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None, // Replace ::ng-deep with ViewEncapsulation.None
  imports: [
    CommonModule,
    ReactiveFormsModule,
    GridModule,
    InputsModule,
    ButtonModule,
    KendoDataSourceMvcDirective
  ]
})
export class TicomixGridWindowComponent<T = any> implements OnInit, OnDestroy, AfterContentInit {
  /** @internal Signal-based properties */
  private _title = signal<string>('Search');
  private _width = signal<number>(800);
  private _height = signal<number>(600);

  /**
   * The title displayed in the window header
   * @default 'Search'
   */
  @Input() set title(value: string) { this._title.set(value); }

  /**
   * The width of the window in pixels
   * @default 800
   */
  @Input() set width(value: number) { this._width.set(value); }

  /**
   * The height of the window in pixels
   * @default 600
   */
  @Input() set height(value: number) { this._height.set(value); }

  /**
   * Array of search fields to display in the search form.
   * Each field defines how the search component will query the data source.
   */
  @Input() searchFields: SearchField[] = [];

  /**
   * Optional HTML content to display above the search fields.
   * Can be used for instructions or additional information.
   */
  @Input() preSearchHTML: string | null = null;

  /**
   * The data source to use for the grid.
   * Must implement the IMvcDataSource interface.
   */
  @Input() dataSource!: IMvcDataSource<T>;

  /**
   * Event emitted when a row is selected in the grid.
   * The component automatically closes after row selection.
   */
  @Output() rowSelect = new EventEmitter<SelectionEvent>();

  /**
   * Event emitted when the window is opened
   */
  @Output() windowOpen = new EventEmitter<void>();

  /**
   * Event emitted when the window is closed
   */
  @Output() windowClose = new EventEmitter<void>();

  /** @internal Reference to the window content template */
  @ViewChild('windowContent', { static: true }) windowContent!: TemplateRef<any>;

  /**
   * Grid columns provided via content projection.
   * If no columns are provided, they will be generated from searchFields.
   */
  @ContentChildren(ColumnComponent) gridColumns?: QueryList<ColumnComponent>;

  /** @internal Array of column components used in the template */
  columnComponents: ColumnComponent[] = [];

  /** @internal Columns generated from searchFields if no columns are provided */
  generatedColumns: ColumnComponent[] = [];

  /** @internal Reference to the grid component */
  @ViewChild('grid') grid!: GridComponent;

  /** @internal Signal tracking window visibility state */
  visible = signal<boolean>(false);

  /** @internal Current filter state for the grid */
  currentFilter: CompositeFilterDescriptor | undefined;

  /** @internal Reference to the Kendo window */
  private windowRef: WindowRef | null = null;
  
  /** @internal Inject DestroyRef for proper cleanup */
  private destroyRef = inject(DestroyRef);
  
  /** @internal Inject Injector for afterNextRender */
  private injector = inject(Injector);

  constructor(private windowService: WindowService) { }

  ngOnInit(): void {
    if (this.searchFields.length === 0) {
      throw new Error('TicomixGridWindowComponent requires at least one search field');
    }

    if (!this.dataSource) {
      throw new Error('TicomixGridWindowComponent requires dataSource input');
    }
  }

  ngAfterContentInit(): void {
    // This lifecycle hook runs after content projection is initialized
    if (this.gridColumns && this.gridColumns.length > 0) {
      this.columnComponents = this.gridColumns.toArray();
    } else {
      // Generate columns from searchFields if no columns are provided
      this.generatedColumns = this.searchFields.map(field => {
        const column = new ColumnComponent();
        column.field = field.fieldName;
        column.title = field.labelText || field.fieldName;
        return column;
      });
      this.columnComponents = this.generatedColumns;
    }
  }

  /** @internal Getter for title signal value */
  get titleValue() { return this._title(); }

  /** @internal Getter for width signal value */
  get widthValue() { return this._width(); }

  /** @internal Getter for height signal value */
  get heightValue() { return this._height(); }

  /**
   * Opens the grid window with an optional initial search value
   * @param initialSearchValue Optional text to set in the first search field and trigger search
   */
  open(initialSearchValue?: string | CompositeFilterDescriptor): void {
    const windowSettings: WindowSettings = {
      title: this.titleValue,
      content: this.windowContent,
      width: this.widthValue,
      height: this.heightValue,
      minWidth: 250,
      minHeight: 250,
      resizable: true,
      draggable: true,
      cssClass: 'ticomix-grid-window'
    };

    this.windowRef = this.windowService.open(windowSettings);

    this.windowRef.result.pipe(
      takeUntilDestroyed(this.destroyRef) // Automatically unsubscribe on component destroy
    ).subscribe(() => {
      this.close();
    });

    this.visible.set(true);
    this.windowOpen.emit();

    if (initialSearchValue && this.searchFields.length > 0) {
      // Use afterNextRender to ensure the grid is initialized
      afterNextRender(() => {
        if (this.grid) {
          this.performInitialSearch(initialSearchValue);
        } else {
          // This fallback is now much less likely to be needed
          console.error('TicomixGridWindowComponent: Grid could not be initialized for initial search.');
        }
      }, { injector: this.injector });
    }
  }

  /**
   * Performs the initial search with the provided value.
   * Extracted method to handle the search logic and prevent race conditions.
   * @param initialSearchValue The initial search value or filter descriptor
   */
  private performInitialSearch(initialSearchValue: string | CompositeFilterDescriptor): void {
    if (typeof initialSearchValue === 'string') {
      const filter: CompositeFilterDescriptor = {
        logic: 'or',
        filters: this.searchFields.map(field => ({
          field: field.fieldName,
          operator: 'contains',
          value: initialSearchValue
        }))
      };
      this.search(filter);
    } else if (typeof initialSearchValue === 'object') {
      // If the initial search value is a filter descriptor, use it directly
      this.search(initialSearchValue);
    }
  }

  /**
   * Closes the window and emits the windowClose event
   */
  close(): void {
    if (this.windowRef) {
      this.windowRef.close();
      this.windowRef = null;
    }
    this.visible.set(false);
    this.windowClose.emit();
  }

  ngOnDestroy(): void {
    if (this.windowRef) {
      this.windowRef.close();
      this.windowRef = null;
    }
  }

  /**
   * Applies a filter to the grid and refreshes the data.
   * @param filter Optional composite filter descriptor to apply
   */
  search(filter?: CompositeFilterDescriptor): void {
    // Apply filters to grid
    if (filter) {
      //Perform a deep copy to avoid mutating the original filter object
      this.currentFilter = JSON.parse(JSON.stringify(filter));
    } else {
      this.currentFilter = {
        logic: 'and',
        filters: []
      };
    }

    // Need to trigger filterChange to refresh the data
    // The [filter] binding only sets UI state, but doesn't trigger data refresh
    if (this.grid) {
      this.grid.filterChange.emit(this.currentFilter);
    }
  }

  /**
   * Clears all filters from the grid and refreshes the data
   */
  clearSearch(): void {
    if (this.grid) {
      // Calling search() with no arguments will clear the filter
      // and trigger a full dataStateChange, ensuring consistent behavior
      this.search();
    }
  }

  /**
   * Handles filter changes from the grid UI.
   * Updates the current filter state to keep it synchronized.
   * @param filter The new filter from the grid
   */
  onFilterChange(filter: CompositeFilterDescriptor): void {
    this.currentFilter = filter;
  }

  /**
   * Handles row selection events from the grid.
   * Emits the selection event and closes the window.
   * @param e The selection event from the Kendo Grid
   */
  onRowSelect(e: SelectionEvent): void {
    this.rowSelect.emit(e);
    this.close();
  }

  /**
   * Gets the label text for a field from the searchFields array
   * @param fieldName The field name to look up
   * @returns The label text or the field name if no label is found
   */
  getSearchFieldLabel(fieldName: string): string {
    const field = this.searchFields.find(f => f.fieldName === fieldName);
    return field?.labelText || fieldName;
  }
}